import { queryOptions } from '@tanstack/react-query';

import { getAuthHeaders } from '@/identity.js';

/**
 * A published release note as served by cti-admin-service
 * (`GET /v1/release-notes` — camelCase wire contract, newest first).
 */
export type ReleaseNoteCategory = 'new' | 'improved' | 'fixed' | 'announcement';

export interface ReleaseNote {
  id: string;
  title: string;
  /** May be null on legacy notes, or an unknown string from a newer server. */
  category: ReleaseNoteCategory | null;
  bodyMd: string;
  publishedAt: string;
}

/**
 * Published release notes, newest first. Fail-quiet by design: an auth,
 * network, or server error resolves to an empty list so the "What's new"
 * affordance never toasts or blocks — an empty feed just renders the empty
 * state and shows no unread dot. The response is type-cast, not validated,
 * per the repo's API convention; `items` is unwrapped defensively.
 */
async function fetchReleaseNotes(limit: number): Promise<ReleaseNote[]> {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`/v1/release-notes?limit=${limit}`, { headers });
    if (!res.ok) return [];
    const body = (await res.json()) as { items?: ReleaseNote[] };
    return Array.isArray(body.items) ? sortNewestFirst(body.items) : [];
  } catch {
    return [];
  }
}

/**
 * Defensive newest-first ordering: the server already sends newest first,
 * but the dialog must not depend on it. Unparsable timestamps sink to the
 * end (stable among themselves).
 */
function sortNewestFirst(notes: ReleaseNote[]): ReleaseNote[] {
  return [...notes].sort((first, second) => {
    const firstTime = Date.parse(first.publishedAt);
    const secondTime = Date.parse(second.publishedAt);
    const firstRank = Number.isNaN(firstTime)
      ? Number.MIN_SAFE_INTEGER
      : firstTime;
    const secondRank = Number.isNaN(secondTime)
      ? Number.MIN_SAFE_INTEGER
      : secondTime;
    return secondRank - firstRank;
  });
}

/**
 * TanStack Query factory for the "What's new" drawer — recent feed. The
 * release-note.published SSE broadcast invalidates this instantly; the
 * refetch interval is only a fallback for clients whose event stream is down.
 */
export function releaseNotesQueryOptions() {
  return queryOptions({
    queryKey: ['release-notes', 20],
    queryFn: () => fetchReleaseNotes(20),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** TanStack Query factory for the full "All updates" archive page. */
export function allReleaseNotesQueryOptions() {
  return queryOptions({
    queryKey: ['release-notes', 100],
    queryFn: () => fetchReleaseNotes(100),
    staleTime: 5 * 60_000,
  });
}

/**
 * The newest `publishedAt` in a fetched feed. The server sends newest-first,
 * but scan for the max anyway so an out-of-order or garbage timestamp can't
 * break the unread computation. Returns null for an empty/unloaded feed.
 */
export function newestPublishedAt(
  notes: ReleaseNote[] | undefined,
): string | null {
  let newest: string | null = null;
  let newestTime = Number.NEGATIVE_INFINITY;
  for (const note of notes ?? []) {
    const time = Date.parse(note.publishedAt);
    if (!Number.isNaN(time) && time > newestTime) {
      newest = note.publishedAt;
      newestTime = time;
    }
  }
  return newest;
}

/**
 * Split the feed into notes published after `seenAt` (fresh — what the user has
 * not seen since last visit) and the rest (previous). Order is preserved, so
 * both stay newest-first. A null or unparsable `seenAt` (never opened, or a
 * garbage stored value) makes every note fresh — a first-time visitor sees all
 * of it as new. Notes with an unparsable timestamp fall into `previous`.
 */
export function partitionBySeen(
  notes: ReleaseNote[] | undefined,
  seenAt: string | null,
): { fresh: ReleaseNote[]; previous: ReleaseNote[] } {
  const list = notes ?? [];
  const seenTime = seenAt === null ? Number.NaN : Date.parse(seenAt);
  if (Number.isNaN(seenTime)) return { fresh: [...list], previous: [] };
  const fresh: ReleaseNote[] = [];
  const previous: ReleaseNote[] = [];
  for (const note of list) {
    const time = Date.parse(note.publishedAt);
    if (!Number.isNaN(time) && time > seenTime) fresh.push(note);
    else previous.push(note);
  }
  return { fresh, previous };
}
